/**
 * Stops Transfer Section
 *
 * ODB's stop entity is always scoped to a (routeId, versionId) pair — there
 * is no standalone-stop CRUD. This panel currently provides a read-only view
 * of the stops in the selected network so the user can confirm what's there.
 * Route-scoped stop editing (add / update / delete / reorder via
 * `createroutestop` and friends) is a follow-up iteration that will hang off
 * the routes panel rather than living here.
 *
 * Verbs and payloads have been wired in `fixedRouteUatClient.ts`
 * (`createRouteStop`, `updateRouteStop`, `deleteRouteStop`, `orderRouteStops`)
 * and corresponding hooks (`useAddRouteStop`, `useUpdateRouteStop`,
 * `useDeleteRouteStop`) are ready for that follow-up UI.
 */

import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';

import type { Environment } from './EnvironmentSelector';
import { DatabaseStopsPanel } from './stops-transfer/DatabaseStopsPanel';
import type { UATNetwork, UATStopListItem } from '../../../services/fixedRouteUatClient';
import { useTranslation, Trans } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
interface StopsTransferSectionProps {
  jwtToken: string;
  environment: Environment;
  networks: UATNetwork[];
  isLoadingNetworks: boolean;
  networkError: string | null;
  selectedProductId: string;
  selectedNetworkId: number | null;
  onProductChange: (productId: string) => void;
  onLoadNetworks: () => void;
  downloadAcknowledged: boolean;
  onDownloadAcknowledgedChange: (acknowledged: boolean) => void;
}

export const StopsTransferSection: React.FC<StopsTransferSectionProps> = ({
  jwtToken,
  environment,
  networks,
  selectedProductId,
  downloadAcknowledged,
}) => {
  const { t } = useTranslation('mapTransfer');
  const [dbSelectedIds, setDbSelectedIds] = useState<Set<string>>(new Set());
  const [, setDatabaseStops] = useState<UATStopListItem[]>([]);

  const selectedNetwork = networks.find((n) => n.productId === selectedProductId);
  const networkType = selectedNetwork?.networkType;
  const writesUnavailable = networkType === 'ODB';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, gap: 1 }}>
      <Alert severity="info" icon={<Icon size={20} name="circle-info" />}>
        <AlertTitle>{t('stops.section.stopEditingRouteScoped')}</AlertTitle>
        <Trans
          i18nKey="stops.section.stopEditingExplanation"
          ns="mapTransfer"
          components={{ strong: <strong /> }}
        />
      </Alert>

      {writesUnavailable && (
        <Alert severity="info" icon={<Icon size={20} name="location-dot" />}>
          {t('stops.section.odbWritesUnavailable')}
        </Alert>
      )}

      {!downloadAcknowledged && (
        <Alert severity="warning">
          {t('stops.section.ackRequired')}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon name="route" size={20} />
        {t('stops.section.readOnlyDescription', { env: environment })}
      </Typography>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <DatabaseStopsPanel
          jwtToken={jwtToken}
          productId={selectedProductId}
          environment={environment}
          selectedIds={dbSelectedIds}
          onSelectionChange={setDbSelectedIds}
          onStopsLoaded={setDatabaseStops}
          disabled={!downloadAcknowledged}
        />
      </Box>
    </Box>
  );
};

export default StopsTransferSection;
